import { useEffect, useRef, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { useAuth, adminApi, formatApiError } from "./AuthContext";

const ALLOWED_ROLES = ["admin", "coordinator", "check_in_staff"];

function extractToken(scanned) {
  // QR encodes either a full https://.../checkin/<token> URL or just the token.
  if (!scanned) return "";
  const s = String(scanned).trim();
  const m = s.match(/\/checkin\/([A-Za-z0-9_-]+)/);
  if (m) return m[1];
  return s;
}

function denverTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("en-US", {
      timeZone: "America/Denver",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function CheckinScanner() {
  const { user, logout } = useAuth();
  const [scanResult, setScanResult] = useState(null);
  const [scanError, setScanError] = useState("");
  const [manualToken, setManualToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [scannerActive, setScannerActive] = useState(false);
  const scannerRef = useRef(null);
  const containerId = "tns-qr-region";
  const lastTokenRef = useRef({ token: null, at: 0 });

  // ---------------- auth gates (rendered at the bottom so hooks order stays stable)
  const blocked = user === false;
  const wrongRole =
    user && user !== false && !ALLOWED_ROLES.includes(user.role);

  const submitToken = useCallback(async (raw) => {
    const token = extractToken(raw);
    if (!token) return;
    // simple debounce — ignore the same QR within 2.5s of the last call
    const nowMs = Date.now();
    if (lastTokenRef.current.token === token && nowMs - lastTokenRef.current.at < 2500) {
      return;
    }
    lastTokenRef.current = { token, at: nowMs };
    setBusy(true);
    setScanError("");
    try {
      const { data } = await adminApi.post("/checkin", { token });
      setScanResult(data);
    } catch (e) {
      setScanResult(null);
      setScanError(
        formatApiError(e.response?.data?.detail) ||
          e.message ||
          "Check-in failed"
      );
    } finally {
      setBusy(false);
    }
  }, []);

  // Start/stop the camera scanner
  const startScanner = useCallback(async () => {
    setScanError("");
    if (scannerActive) return;
    try {
      const instance = new Html5Qrcode(containerId);
      scannerRef.current = instance;
      await instance.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 260, height: 260 } },
        (decodedText) => {
          submitToken(decodedText);
        },
        () => {
          /* per-frame errors are noisy and harmless */
        }
      );
      setScannerActive(true);
    } catch (e) {
      setScannerActive(false);
      scannerRef.current = null;
      setScanError(
        "Camera unavailable — use manual entry below. (" + (e?.message || e) + ")"
      );
    }
  }, [scannerActive, submitToken]);

  const stopScanner = useCallback(async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
      } catch {
        /* ignore */
      }
      try {
        await scannerRef.current.clear();
      } catch {
        /* ignore */
      }
      scannerRef.current = null;
    }
    setScannerActive(false);
  }, []);

  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, [stopScanner]);

  if (user === null)
    return <Center>Loading…</Center>;
  if (blocked) return <Navigate to="/admin/login" replace />;
  if (wrongRole)
    return (
      <Center>
        <div data-testid="checkin-forbidden" style={{ color: "#ff6b6b" }}>
          You don't have permission to use the check-in scanner.
        </div>
      </Center>
    );

  return (
    <div
      data-testid="checkin-scanner-page"
      style={{
        minHeight: "100vh",
        background: "#0A0F1F",
        color: "#fff",
        fontFamily: "system-ui, sans-serif",
        padding: "32px 24px",
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          borderBottom: "1px solid #14213D",
          paddingBottom: 16,
          marginBottom: 28,
        }}
      >
        <div>
          <div style={{ fontSize: 12, opacity: 0.6, letterSpacing: 2 }}>
            THE NEXT STAGE — DOOR CHECK-IN
          </div>
          <div style={{ marginTop: 6 }}>
            Staff: <strong>{user.name}</strong>{" "}
            <span
              style={{
                padding: "2px 8px",
                background: "#E5B25D",
                color: "#0A0F1F",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 1,
                marginLeft: 6,
              }}
            >
              {user.role.toUpperCase()}
            </span>
          </div>
        </div>
        <button onClick={logout} style={btnSecondary} data-testid="checkin-logout">
          Logout
        </button>
      </header>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(280px, 360px) 1fr",
          gap: 32,
          alignItems: "start",
        }}
      >
        <section>
          <h2 style={{ fontSize: 14, letterSpacing: 1, margin: "0 0 12px 0" }}>
            SCAN A QR
          </h2>
          <div
            id={containerId}
            data-testid="checkin-qr-region"
            style={{
              width: "100%",
              minHeight: 280,
              background: "#000",
              border: "1px solid #14213D",
            }}
          />
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            {!scannerActive ? (
              <button
                onClick={startScanner}
                style={btnPrimary}
                data-testid="checkin-start-camera"
              >
                Start camera
              </button>
            ) : (
              <button
                onClick={stopScanner}
                style={btnSecondary}
                data-testid="checkin-stop-camera"
              >
                Stop camera
              </button>
            )}
          </div>

          <h2 style={{ fontSize: 14, letterSpacing: 1, margin: "28px 0 12px 0" }}>
            MANUAL ENTRY
          </h2>
          <form
            data-testid="checkin-manual-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (!manualToken.trim()) return;
              submitToken(manualToken.trim());
              setManualToken("");
            }}
            style={{ display: "flex", gap: 8 }}
          >
            <input
              data-testid="checkin-manual-input"
              autoComplete="off"
              placeholder="Paste token or scan target"
              value={manualToken}
              onChange={(e) => setManualToken(e.target.value)}
              style={{
                ...inputStyle,
                flex: 1,
              }}
            />
            <button
              data-testid="checkin-manual-submit"
              type="submit"
              disabled={busy || !manualToken.trim()}
              style={btnPrimary}
            >
              {busy ? "…" : "Check in"}
            </button>
          </form>
        </section>

        <section>
          <h2 style={{ fontSize: 14, letterSpacing: 1, margin: "0 0 12px 0" }}>
            LAST SCAN
          </h2>
          {scanError && (
            <ResultCard
              testid="checkin-result-error"
              border="#ff6b6b"
              title="Couldn't check in"
              detail={scanError}
            />
          )}
          {!scanError && !scanResult && (
            <div
              data-testid="checkin-idle"
              style={{
                padding: 28,
                border: "1px dashed #2a3556",
                opacity: 0.6,
                textAlign: "center",
              }}
            >
              Waiting for next scan…
            </div>
          )}
          {!scanError && scanResult && <ResultDisplay result={scanResult} />}
        </section>
      </div>
    </div>
  );
}

function ResultDisplay({ result }) {
  const isCheckedIn = result.result === "checked_in";
  const isAlready = result.result === "already_checked_in";
  const time = denverTime(result.checked_in_at);
  return (
    <ResultCard
      testid={isCheckedIn ? "checkin-result-success" : "checkin-result-already"}
      border={isCheckedIn ? "#5dd6a4" : "#E5B25D"}
      title={
        isCheckedIn
          ? `✓ Checked in at ${time} MT`
          : isAlready
          ? `⚠ Already checked in at ${time} MT`
          : "Result"
      }
      detail={
        <>
          <Row label="Attendee" value={result.attendee_name} />
          <Row label="Tier" value={result.tier_name} />
          <Row label="Event" value={result.event_name} />
          <Row label="Order" value={result.order_id} mono />
          <Row label="Ticket" value={result.ticket_id} mono />
        </>
      }
    />
  );
}

function ResultCard({ testid, border, title, detail }) {
  return (
    <div
      data-testid={testid}
      style={{
        padding: 22,
        border: `2px solid ${border}`,
        background: "#101C35",
      }}
    >
      <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 10 }}>{title}</div>
      <div style={{ fontSize: 13, lineHeight: 1.6 }}>{detail}</div>
    </div>
  );
}

function Row({ label, value, mono = false }) {
  return (
    <div style={{ display: "flex", gap: 12, alignItems: "baseline" }}>
      <span style={{ width: 72, fontSize: 10, opacity: 0.6, letterSpacing: 2 }}>
        {label.toUpperCase()}
      </span>
      <span
        style={{
          flex: 1,
          fontFamily: mono ? "monospace" : "inherit",
          fontSize: mono ? 11 : 14,
        }}
      >
        {value || "—"}
      </span>
    </div>
  );
}

function Center({ children }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        background: "#0A0F1F",
        color: "#fff",
        fontFamily: "system-ui, sans-serif",
      }}
    >
      {children}
    </div>
  );
}

const inputStyle = {
  padding: "10px 12px",
  background: "#0A0F1F",
  border: "1px solid #2a3556",
  color: "#fff",
  borderRadius: 2,
  fontSize: 14,
};
const btnPrimary = {
  padding: "10px 16px",
  background: "#E5B25D",
  color: "#0A0F1F",
  border: 0,
  fontWeight: 700,
  cursor: "pointer",
};
const btnSecondary = {
  padding: "10px 16px",
  background: "transparent",
  color: "#fff",
  border: "1px solid #2a3556",
  cursor: "pointer",
};
